/**
 * Corpus stats — what the current selection actually contains, by task and by
 * setting.
 *
 * Lives on Analysis rather than Datasets because it answers a question about the
 * corpus ("what have we got, and in what mix"), not about a delivery. Analysis
 * already holds the same selection the projections price, so the panel and the
 * rate on screen describe the same footage.
 *
 * Two donuts, one denominator each, and they are deliberately different:
 *   - task categories are a share of TAGGED footage, because a video with no
 *     activity tag is not in any category and pretending it is would inflate
 *     every slice;
 *   - environments are a share of ALL footage, because every video has one --
 *     read off the frames when CLIP was confident, otherwise the customer's
 *     typical setting. The legend says how many are which, since a setting mix
 *     that is mostly inferred is a weaker claim than one that is mostly seen.
 *
 * Reuses DatasetPage.css: the modal, table and donut classes are all defined
 * there, and this panel is the same visual object that used to live inside it.
 */
import React, { useMemo, useState } from 'react';

import { CorpusVideo, EnvCoverage, EnvLabel, TOP_ACTIVITIES, fmtHours } from './whShared';
import './DatasetPage.css';

// Categorical slots in fixed order (CVD-validated adjacency), assigned by size
// rank. Never more than 7 real slices — the tail folds into a gray "Other",
// because gray is not-a-series by design.
const CAT_COLORS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7',
];
const OTHER_COLOR = '#98a2b3';

/** SVG path for one donut slice, angles in radians from 12 o'clock. */
const sliceArc = (
  cx: number, cy: number, r0: number, r1: number, a0: number, a1: number,
) => {
  const p = (r: number, a: number) => `${cx + r * Math.cos(a)} ${cy + r * Math.sin(a)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return (
    `M${p(r1, a0)} A${r1} ${r1} 0 ${large} 1 ${p(r1, a1)} ` +
    `L${p(r0, a1)} A${r0} ${r0} 0 ${large} 0 ${p(r0, a0)} Z`
  );
};

const fmtPct = (p: number) => `${p >= 1 ? p.toFixed(0) : p.toFixed(1)}%`;

interface Slice {
  label: string;
  seconds: number;
}

/** One donut + its legend. The legend IS the labeling: several slice colors sit
 *  below 3:1 on white, so identity has to be readable as text. */
function Donut({
  title, hint, slices, centerLabel, note,
}: {
  title: string;
  hint: string;
  slices: Slice[];
  centerLabel: string;
  note: React.ReactNode;
}) {
  // The tail can run to two dozen rows and push the note off screen, so it is
  // collapsed by default: the gray slice already carries its total, and the
  // itemization is a detail you ask for.
  const [openRest, setOpenRest] = useState(false);
  const top = slices.slice(0, CAT_COLORS.length);
  const rest = slices.slice(CAT_COLORS.length);
  const restSec = rest.reduce((a, r) => a + r.seconds, 0);
  const shown = [
    ...top.map((r, i) => ({ ...r, color: CAT_COLORS[i] })),
    ...(restSec > 0
      ? [{ label: `Other (${rest.length})`, seconds: restSec, color: OTHER_COLOR }]
      : []),
  ];
  const total = slices.reduce((a, r) => a + r.seconds, 0) || 1;
  let a = -Math.PI / 2;
  const arcs = shown.map((s) => {
    const span = Math.min((s.seconds / total) * 2 * Math.PI, 2 * Math.PI - 1e-3);
    const d = sliceArc(100, 100, 56, 90, a, a + span);
    a += span;
    return { ...s, d, pct: (100 * s.seconds) / total };
  });
  return (
    <div className="ds-stats-pane">
      <div className="ds-checkout-label">
        {title}
        <span className="ds-checkout-sub">{hint}</span>
      </div>
      <div className="ds-stats-hero">
        <svg viewBox="0 0 200 200" className="ds-donut" role="img" aria-label={hint}>
          {arcs.map((s) => (
            <path key={s.label} d={s.d} fill={s.color} stroke="#fff" strokeWidth="2">
              <title>
                {`${s.label} — ${fmtHours(s.seconds / 3600)} (${fmtPct(s.pct)})`}
              </title>
            </path>
          ))}
          <text x="100" y="96" className="ds-donut-n">
            {fmtHours(total / 3600)}
          </text>
          <text x="100" y="114" className="ds-donut-l">
            {centerLabel}
          </text>
        </svg>
        <div className="ds-stats-legend">
          {arcs.map((s) => {
            const isRest = s.color === OTHER_COLOR && rest.length > 0;
            const row = (
              <>
                <i style={{ background: s.color }} />
                <span className="ds-stats-lname" title={s.label}>
                  {s.label}
                  {isRest && (
                    <span className="ds-stats-caret" aria-hidden="true">
                      {openRest ? '▲' : '▼'}
                    </span>
                  )}
                </span>
                <span className="ds-orgmix-pct">{fmtPct(s.pct)}</span>
                <span className="ds-orgmix-amt">{fmtHours(s.seconds / 3600)}</span>
              </>
            );
            return isRest ? (
              <button
                type="button"
                className="ds-stats-lrow ds-stats-lbtn"
                key={s.label}
                aria-expanded={openRest}
                onClick={() => setOpenRest((v) => !v)}
                title={openRest ? 'Hide the tail' : `Itemize all ${rest.length}`}
              >
                {row}
              </button>
            ) : (
              <div className="ds-stats-lrow" key={s.label}>{row}</div>
            );
          })}
          {/* The Other slice, itemized: hollow chips because these share the one
              gray slice rather than owning colors. */}
          {openRest &&
            rest.map((r) => (
              <div className="ds-stats-lrow ds-stats-sub" key={r.label}>
                <i className="ds-chip-sub" />
                <span className="ds-stats-lname" title={r.label}>{r.label}</span>
                <span className="ds-orgmix-pct">{fmtPct((100 * r.seconds) / total)}</span>
                <span className="ds-orgmix-amt">{fmtHours(r.seconds / 3600)}</span>
              </div>
            ))}
          <div className="ds-hint">{note}</div>
        </div>
      </div>
    </div>
  );
}

export default function StatsPanel({
  videos, taskOf, envs, coverage, warming, onClose,
}: {
  /** The selection, already filtered — this panel never re-filters. */
  videos: CorpusVideo[];
  taskOf: Map<string, string>;
  envs: { [fn: string]: EnvLabel };
  coverage: EnvCoverage | null;
  /** Activity queries still in flight; the numbers below grow until it is 0. */
  warming: number;
  onClose: () => void;
}) {
  const catStats = useMemo(() => {
    const byFn = new Map(videos.map((v) => [v.filename, v]));
    const cats = new Map<
      string, { seconds: number; videos: number; inds: Map<string, number> }
    >();
    let taggedSeconds = 0;
    taskOf.forEach((label, fn) => {
      const v = byFn.get(fn);
      if (!v) return;
      const e = cats.get(label) || { seconds: 0, videos: 0, inds: new Map() };
      e.seconds += v.duration;
      e.videos += 1;
      e.inds.set(v.industry, (e.inds.get(v.industry) || 0) + v.duration);
      cats.set(label, e);
      taggedSeconds += v.duration;
    });
    const rows = Array.from(cats.entries())
      .map(([label, e]) => ({
        label, ...e, pct: (e.seconds / (taggedSeconds || 1)) * 100,
      }))
      .sort((a, b) => b.seconds - a.seconds);
    const indTotals = new Map<string, number>();
    rows.forEach((r) =>
      r.inds.forEach((s, ind) => indTotals.set(ind, (indTotals.get(ind) || 0) + s)),
    );
    const industries = Array.from(indTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([ind]) => ind);
    const totalSeconds = videos.reduce((a, v) => a + v.duration, 0);
    return { rows, industries, taggedSeconds, totalSeconds };
  }, [videos, taskOf]);

  // Environments partition the selection: exactly one per video, and every video
  // has one. `visual` is tracked alongside because an inferred setting is the
  // customer's industry default, not an observation of that clip.
  const envStats = useMemo(() => {
    const m = new Map<string, { seconds: number; videos: number; visual: number }>();
    let visualSeconds = 0;
    videos.forEach((v) => {
      const e = envs[v.filename];
      const key = e?.environment || 'setting unknown';
      const row = m.get(key) || { seconds: 0, videos: 0, visual: 0 };
      row.seconds += v.duration;
      row.videos += 1;
      if (e?.source === 'visual') {
        row.visual += v.duration;
        visualSeconds += v.duration;
      }
      m.set(key, row);
    });
    const rows = Array.from(m.entries())
      .map(([label, e]) => ({ label, ...e }))
      .sort((a, b) => b.seconds - a.seconds);
    return { rows, visualSeconds, totalSeconds: catStats.totalSeconds };
  }, [videos, envs, catStats.totalSeconds]);

  const haveEnvs = envStats.rows.length > 0 && Object.keys(envs).length > 0;

  return (
    <div className="ds-modal" onClick={onClose}>
      <div className="ds-checkout-box ds-stats-box" onClick={(e) => e.stopPropagation()}>
        <div className="ds-checkout-head">
          <b>Corpus stats</b>
          <button onClick={onClose}>close</button>
        </div>

        {warming > 0 && (
          <div className="ds-hint">
            still tagging — {warming} activity {warming === 1 ? 'query' : 'queries'}{' '}
            to go, category numbers below will grow
          </div>
        )}

        {/* The two donuts are independent: task tags arrive from 42 background
            searches and environments from a separate cache, so either can be
            ready first and neither waits on the other. */}
        <div className="ds-stats-panes">
          {catStats.rows.length > 0 ? (
            <Donut
              title="By task"
              hint="share of tagged footage by activity category"
              centerLabel="tagged"
              slices={catStats.rows.map((r) => ({ label: r.label, seconds: r.seconds }))}
              note={
                <>
                  {fmtHours(catStats.taggedSeconds / 3600)} of{' '}
                  {fmtHours(catStats.totalSeconds / 3600)} in this selection carries a
                  task tag
                </>
              }
            />
          ) : (
            <div className="ds-stats-pane">
              <div className="ds-checkout-label">By task</div>
              <div className="ds-hint">
                {warming > 0
                  ? 'tagging the corpus — the category mix appears as queries answer'
                  : 'no tagged footage in this selection'}
              </div>
            </div>
          )}
          {haveEnvs && (
            <Donut
              title="By environment"
              hint="share of all footage by the setting it was shot in"
              centerLabel="all footage"
              slices={envStats.rows.map((r) => ({
                label: r.label, seconds: r.seconds,
              }))}
              note={
                <>
                  {fmtHours(envStats.visualSeconds / 3600)} of{' '}
                  {fmtHours(envStats.totalSeconds / 3600)} was read off the frames
                  {coverage && coverage.pending > 0
                    ? ` — ${coverage.pending.toLocaleString()} videos still to classify`
                    : ''}
                  ; the rest is the customer&rsquo;s typical setting
                </>
              }
            />
          )}
        </div>

        {catStats.rows.length > 0 && (
          <div className="ds-checkout-sec">
            <div className="ds-checkout-label">
              By industry
              <span className="ds-checkout-sub">hours of tagged footage</span>
            </div>
            <div className="ds-checkout-table ds-stats-matrix">
              <table className="ds-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    {catStats.industries.map((ind) => (
                      <th className="num" key={ind}>{ind}</th>
                    ))}
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {catStats.rows.map((r) => (
                    <tr key={r.label}>
                      <td className="ds-q ds-stats-cat">
                        {r.label}
                        <div className="ds-stats-qs">
                          {TOP_ACTIVITIES.find((a) => a.label === r.label)
                            ?.queries.join(' · ')}
                        </div>
                      </td>
                      {catStats.industries.map((ind) => {
                        const s = r.inds.get(ind);
                        return (
                          <td className="num" key={ind}>
                            {s ? fmtHours(s / 3600) : '—'}
                          </td>
                        );
                      })}
                      <td className="num"><b>{fmtHours(r.seconds / 3600)}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {haveEnvs && (
          <div className="ds-checkout-sec">
            <div className="ds-checkout-label">
              Environments
              <span className="ds-checkout-sub">
                videos, hours, and how much of each was seen rather than inferred
              </span>
            </div>
            <div className="ds-checkout-table ds-stats-matrix">
              <table className="ds-table">
                <thead>
                  <tr>
                    <th>Environment</th>
                    <th className="num">Videos</th>
                    <th className="num">Hours</th>
                    <th className="num">Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {envStats.rows.map((r) => (
                    <tr key={r.label}>
                      <td className="ds-q">{r.label}</td>
                      <td className="num">{r.videos.toLocaleString()}</td>
                      <td className="num"><b>{fmtHours(r.seconds / 3600)}</b></td>
                      <td className="num">
                        {r.seconds ? fmtPct((100 * r.visual) / r.seconds) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
